import { CompletionStatusType } from "@/components/refineRealEstate/dataGroupsConstants";
import { OneClickPropertyDetails } from "@/pages/oneClickPage/oneClickPageSlice";
import { PropertyInformation } from "@/types/refineRealEstate/refineRealEstate";
import {
  BuildingData,
  EnergyAndPhotovoltaic,
  FinancialAndRentalInfo,
  Modernizations,
  PropertyImages,
} from "@/types/refineRealEstate/refineRealEstate";
import {
  BasementEnum,
  BuildingTypeEnum,
  EnergyCertificateEnum,
  EnergyEfficiencyHouseStandardEnum,
  ModernizationMethodEnum,
  ObjectTypeEnum,
} from "@/types/refineRealEstate/valuation";

import {
  isValidCommercialArea,
  isValidCommercialUnits,
  isValidConstructionYear,
  isValidEnergyCertificateType,
  isValidLivingArea,
  isValidNumberOfFloors,
  isValidPVConstructionYear,
  isValidPVYield,
  isValidPlotArea,
  isValidRefurbishmentYear,
  isValidUnitsNumber,
} from "./dataValidationHelpers";

export const calculateCompleteness = (propertyDataState: PropertyInformation | OneClickPropertyDetails): number => {
  const weights: Record<string, number> = {
    plotArea: propertyDataState.generalInfo.objectType === ObjectTypeEnum.plotOfLand ? 0.7 : 0.15,
    objectType: 0.1,
    ownership: 0.1,
    constructionYear: 0.2,
    livingArea: 0.2,
    energyEfficiency: 0.15,
    photovoltaicSystem: 0.1,
  };

  const isPhotovoltaicSystemValid = (system: EnergyAndPhotovoltaic): boolean => {
    const { photovoltaicAvailable, constructionYear, annualPowerYield } = system;

    if (!photovoltaicAvailable) return true;

    return (
      constructionYear != null &&
      annualPowerYield != null &&
      isValidPVConstructionYear(photovoltaicAvailable, constructionYear) &&
      isValidPVYield(photovoltaicAvailable, annualPowerYield)
    );
  };

  const checks = [
    {
      isValid: !!propertyDataState.generalInfo.objectType,
      weight: weights.objectType,
    },
    {
      isValid: !!propertyDataState.generalInfo.ownership,
      weight: weights.ownership,
    },
    {
      isValid:
        propertyDataState.generalInfo.objectType === ObjectTypeEnum.apartment
          ? true
          : isValidPlotArea(propertyDataState.plotData.plotArea),
      weight: weights.plotArea,
    },
    {
      isValid:
        isValidConstructionYear(propertyDataState.buildingData.constructionYear) &&
        (propertyDataState.generalInfo.objectType !== ObjectTypeEnum.mixedUseProperty ||
          (isValidCommercialArea(propertyDataState.buildingData.commercialArea) &&
            isValidCommercialUnits(propertyDataState.buildingData.commercialUnits))),
      weight: weights.constructionYear,
    },
    {
      isValid: isValidLivingArea(
        propertyDataState.buildingData.livingArea,
        propertyDataState.generalInfo.objectType,
        propertyDataState.buildingData.unitsNumber,
      ),
      weight: weights.livingArea,
    },
    {
      isValid: hasEnergyEfficiencyInfo(
        propertyDataState.energyAndPhotovoltaic.energyCertificate,
        propertyDataState.energyAndPhotovoltaic.energyEfficiencyHouseStandard,
      ),
      weight: weights.energyEfficiency,
    },
    {
      isValid:
        propertyDataState.generalInfo.objectType === ObjectTypeEnum.apartment
          ? true
          : isPhotovoltaicSystemValid(propertyDataState.energyAndPhotovoltaic),
      weight: weights.photovoltaicSystem,
    },
  ];

  const totalWeight = checks.reduce((sum, check) => sum + (check.isValid ? check.weight : 0), 0);
  return Math.round(totalWeight * 100);
};

export const hasEnergyEfficiencyInfo = (
  energyClass?: EnergyCertificateEnum | null | string,
  buildingStandard?: EnergyEfficiencyHouseStandardEnum | null | string,
): boolean =>
  (energyClass !== undefined && energyClass !== null && energyClass !== "") ||
  (buildingStandard !== undefined && buildingStandard !== null && buildingStandard !== "");

export const buildingDataCompletionStatus = (
  buildingData: Omit<BuildingData, "completionStatus">,
  objectType?: ObjectTypeEnum | null,
  buildingType?: BuildingTypeEnum | null,
): CompletionStatusType => {
  const { constructionYear, livingArea, construction, unitsNumber, numberOfFloors, commercialArea, commercialUnits } =
    buildingData;

  const isHouse = objectType === ObjectTypeEnum.house;
  const isApartment = buildingType === BuildingTypeEnum.apartment;
  const isMultiFamilyHouse = buildingType === BuildingTypeEnum.multiFamilyHouse;
  const isMixedUseProperty = buildingType === BuildingTypeEnum.mixedUseProperty;

  // Check minimal required fields
  const hasRequiredConstruction = isHouse ? Boolean(construction) : true;
  const hasValidUnitsNumber =
    isMultiFamilyHouse || isMixedUseProperty
      ? isValidUnitsNumber(unitsNumber, objectType)
      : isApartment
        ? Boolean(unitsNumber)
        : true;
  const hasRequiredNumberOfFloors =
    isHouse || isMultiFamilyHouse || isMixedUseProperty ? isValidNumberOfFloors(numberOfFloors) : true;
  const hasRequiredCommercialData =
    !isMixedUseProperty || (isValidCommercialArea(commercialArea) && isValidCommercialUnits(commercialUnits));

  const hasMinimalFields =
    isValidConstructionYear(constructionYear) &&
    isValidLivingArea(livingArea, objectType, unitsNumber) &&
    hasRequiredConstruction &&
    hasValidUnitsNumber &&
    hasRequiredNumberOfFloors &&
    hasRequiredCommercialData;

  if (!hasMinimalFields) {
    return "inProgress";
  }

  let hasAllOptionalFields = true;

  if (isHouse) {
    const houseFields = [
      buildingData.houseOrApartmentType !== null && buildingData.houseOrApartmentType !== undefined,
      buildingData.numberOfFloors !== null && buildingData.numberOfFloors !== undefined,
      buildingData.rooms !== null && buildingData.rooms !== undefined,
      buildingData.roof !== null && buildingData.roof !== undefined,
      buildingData.basement !== null && buildingData.basement !== undefined,
      buildingData.outdoorFacilities !== null && buildingData.outdoorFacilities !== undefined,
      buildingData.garages !== null && buildingData.garages !== undefined,
    ];

    // basementConversion is only required if basement is not none
    if (buildingData.basement !== BasementEnum.none) {
      houseFields.push(buildingData.basementConversion !== null && buildingData.basementConversion !== undefined);
    }

    hasAllOptionalFields = houseFields.every(Boolean);
  } else if (isApartment) {
    // For apartments, check additional required fields
    const apartmentFields = [
      buildingData.rooms !== null && buildingData.rooms !== undefined,
      buildingData.floorNumber !== null && buildingData.floorNumber !== undefined,
      buildingData.garden !== null && buildingData.garden !== undefined,
      buildingData.elevator !== null && buildingData.elevator !== undefined,
      buildingData.garages !== null && buildingData.garages !== undefined,
      buildingData.outdoorParkingSpaces !== null && buildingData.outdoorParkingSpaces !== undefined,
    ];

    hasAllOptionalFields = apartmentFields.every(Boolean);
  } else if (isMultiFamilyHouse) {
    // For multi-family houses, check additional required fields
    const multiFamilyFields = [
      buildingData.numberOfFloors !== null && buildingData.numberOfFloors !== undefined,
      buildingData.unitsNumber !== null && buildingData.unitsNumber !== undefined,
      buildingData.elevator !== null && buildingData.elevator !== undefined,
      buildingData.garages !== null && buildingData.garages !== undefined,
    ];

    hasAllOptionalFields = multiFamilyFields.every(Boolean);
  }

  return hasAllOptionalFields ? "completedFull" : "completedMinimal";
};

export const plotDataCompletionStatus = (plotArea?: number | null): CompletionStatusType => {
  if (isValidPlotArea(plotArea)) {
    return "completedFull";
  }
  return "notStarted";
};

export const energyAndPhotovoltaicCompletionStatus = (
  energyAndPhotovoltaic: Omit<EnergyAndPhotovoltaic, "completionStatus">,
  buildingConstructionYear?: number | null,
): CompletionStatusType => {
  const {
    energyCertificate,
    energyEfficiencyHouseStandard,
    issueDate,
    certificateType,
    finalEnergy,
    primaryEnergy,
    CO2Emissions,
    photovoltaicAvailable,
    constructionYear: pvConstructionYear,
    annualPowerYield,
  } = energyAndPhotovoltaic;

  if (!isValidEnergyCertificateType(energyCertificate, certificateType)) {
    return "inProgress";
  }

  // Check if PV fields are valid when PV is available
  if (photovoltaicAvailable === true) {
    if (
      !isValidPVConstructionYear(photovoltaicAvailable, pvConstructionYear, buildingConstructionYear) ||
      !isValidPVYield(photovoltaicAvailable, annualPowerYield)
    ) {
      return "inProgress";
    }
  }

  // Check if at least one energy efficiency field is filled (minimal requirement)
  const hasMinimalEnergyInfo = hasEnergyEfficiencyInfo(energyCertificate, energyEfficiencyHouseStandard);

  if (!hasMinimalEnergyInfo) {
    return "completedMinimal"; // Even without energy info, it's considered minimal
  }

  // Check for full completion: both energy fields + all certificate details if certificate is present
  const hasBothEnergyFields = Boolean(energyCertificate) && Boolean(energyEfficiencyHouseStandard);

  let hasCertificateDetails = true;
  if (energyCertificate) {
    hasCertificateDetails =
      issueDate != null &&
      certificateType != null &&
      finalEnergy != null &&
      primaryEnergy != null &&
      CO2Emissions != null;
  }

  if (hasBothEnergyFields && hasCertificateDetails) {
    return "completedFull";
  }

  return "completedMinimal";
};

export const financialAndRentalInfoCompletionStatus = (
  financialAndRentalInfo: Omit<FinancialAndRentalInfo, "completionStatus">,
): CompletionStatusType => {
  return financialAndRentalInfo.rent?.value != null ? "completedFull" : "notStarted";
};

export const modernizationCompletionStatus = (
  modernization: Omit<Modernizations, "completionStatus">,
  buildingConstructionYear?: number | null,
): CompletionStatusType => {
  const { method, refurbishmentYear, class: modernizationClass, cost, costDescription } = modernization;
  const hasYear = refurbishmentYear != null;

  if (method === ModernizationMethodEnum.automatic) {
    return "completedFull";
  }

  if (hasYear && !isValidRefurbishmentYear(refurbishmentYear, buildingConstructionYear)) {
    return "inProgress";
  }

  if (method === ModernizationMethodEnum.costs) {
    const hasCost = cost != null && cost > 0;
    const hasCostDescription = costDescription != null && costDescription.trim() !== "";

    if (!hasCost) {
      return "inProgress";
    }

    return hasYear && hasCostDescription ? "completedFull" : "completedMinimal";
  }

  if (method === ModernizationMethodEnum.simple) {
    if (modernizationClass == null) {
      return "inProgress";
    }

    return hasYear ? "completedFull" : "completedMinimal";
  }

  if (method === ModernizationMethodEnum.detailed) {
    return hasYear ? "completedFull" : "completedMinimal";
  }

  return "inProgress";
};

export const propertyImagesCompletionStatus = (
  images?: PropertyImages["images"] | null,
  pendingImages?: unknown[] | null,
): CompletionStatusType => {
  return images?.length || pendingImages?.length ? "completedFull" : "notStarted";
};
